import { useQuery } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  AddOn,
  addAddOn,
  removeAddOn,
  selectAddOns,
} from '../features/addOns/addOnsSlice';
import { Appointment } from '../features/serviceLocation/serviceLocationSlice';
import { selectServiceTypes } from '../features/serviceTypes/serviceTypesSlice';
import { ServiceType } from '../interfaces/WorkOrder';
import { GET_ADD_ON_SURCHARGE_PRICES } from '../util/gql';
import { ServiceNameKeys } from '../util/serviceKeys';
import { isSlotToday } from '../util/slotTime';

type MobileKey =
  | typeof ServiceNameKeys.mobileServiceFee
  | typeof ServiceNameKeys.mobileServiceFeeSubscription
  | typeof ServiceNameKeys.mobileServiceFeeAllInclusiveSubscription
  | typeof ServiceNameKeys.mobileServiceFeeAMZ
  | typeof ServiceNameKeys.mobileServiceFeeAMZMainService;

const mobileVariants = [
  ServiceNameKeys.mobileServiceFee,
  ServiceNameKeys.mobileServiceFeeSubscription,
  ServiceNameKeys.mobileServiceFeeAllInclusiveSubscription,
  ServiceNameKeys.mobileServiceFeeAMZ,
  ServiceNameKeys.mobileServiceFeeAMZMainService,
] as const;

function isMobileVariant(k: string): k is (typeof mobileVariants)[number] {
  const list: readonly string[] = mobileVariants;
  return list.includes(k);
}

/**
 * The fee lines a booking carries once its time is picked: supplies / shop
 * fee, environmental handling (oil and plan visits), the mobile service fee
 * that fits the basket, Amazon's U-bolt retorque and the same-day priority
 * fee. Moved out of routes/Schedule.tsx unchanged so the consumer skin's
 * day step puts the same lines in the same basket (the server re-prices the
 * booking from them and refuses a mismatch).
 */
export default function useSlotFees(
  vendorId: number | null,
  /**
   * `sameDayFee: false`: never add the same-day priority fee (the consumer
   * skin: no extra charge for today, owner 2026-10-21), and take it out of
   * a basket that has it. Today's flow keeps it.
   */
  options: { sameDayFee?: boolean } = {},
) {
  const sameDayFeeAllowed = options.sameDayFee !== false;
  const dispatch = useAppDispatch();
  const selectedServices = useAppSelector(selectServiceTypes);
  const selectedAddOns = useAppSelector(selectAddOns);
  const carId = useAppSelector((state) => state.car.id);
  const customerId = useAppSelector((state) => state.customer.id);
  const userVehicleId = useAppSelector((state) => state.car.userVehicleId);

  const hasSubscription = useMemo(
    () => selectedServices.some(
      (s) => s.serviceTypeName === ServiceType.subscription,
    ),
    [selectedServices],
  );

  const addAddOnPriced = useCallback((addon: AddOn) => {
    const isPriority = addon.nameKey === ServiceNameKeys.priorityServiceFee;
    const isMobile = typeof addon.nameKey === 'string'
    && isMobileVariant(addon.nameKey);
    const payload: AddOn = isPriority || isMobile
      ? addon
      : {
        ...addon,
        value: hasSubscription ? 0 : addon.value,
        discountedValue: hasSubscription ? 0 : addon.discountedValue,
      };
    dispatch(addAddOn(payload));
  }, [dispatch, hasSubscription]);

  const { data } = useQuery(GET_ADD_ON_SURCHARGE_PRICES, {
    variables: {
      id: carId, vendorId, userVehicleId, customerId,
    },
    fetchPolicy: 'network-only',
  });

  const applySlotFees = useCallback((date: Appointment) => {
    if (data) {
      const shopFee = data.getAddOnSurchargePrices.find(
        (addOn: AddOn) => addOn.nameKey === ServiceNameKeys.suppliesAndShopFee,
      );
      const environmentFee = data.getAddOnSurchargePrices.find(
        (addOn: AddOn) => addOn.nameKey
        === ServiceNameKeys.environmentalHandlingFee,
      );
      const alreadyAddedShopFee = shopFee && selectedAddOns.some(
        (addOn: AddOn) => addOn.id === shopFee.id,
      );
      // A tenant with no environmental fee row must not blank the step.
      const alreadyAddedEnvironmentFee = !!environmentFee
        && selectedAddOns.some(
          (addOn: AddOn) => addOn.id === environmentFee?.id,
        );
      if (shopFee && !alreadyAddedShopFee) {
        addAddOnPriced(shopFee);
      } else if (alreadyAddedShopFee && !shopFee) {
        dispatch(removeAddOn(shopFee.id));
      }

      if (
        environmentFee
        && selectedServices.some(
          (service) => service.serviceTypeName === ServiceType.oilChange
          || service.serviceTypeName === ServiceType.subscription,
        )
        && !alreadyAddedEnvironmentFee
      ) {
        addAddOnPriced(environmentFee);
      } else if (
        alreadyAddedEnvironmentFee
        && !selectedServices.some(
          (service) => service.serviceTypeName === ServiceType.oilChange
          || service.serviceTypeName === ServiceType.subscription,
        )
      ) {
        dispatch(removeAddOn(environmentFee.id));
      } else if (alreadyAddedEnvironmentFee && !environmentFee) {
        dispatch(removeAddOn(environmentFee.id));
      }

      // Mobile fee logic
      const hasMonthly = selectedServices.some(
        (s) => s.nameKey === ServiceNameKeys.subscribeToMonthly,
      );
      const hasAnnual = selectedServices.some(
        (s) => s.nameKey === ServiceNameKeys.subscribeToAnnual,
      );
      const hasAllInclusiveAnnual = selectedServices.some(
        (s) => s.nameKey === ServiceNameKeys.subscribeToAllInclusiveAnnual,
      );

      let mobileKey: MobileKey;
      if (hasAllInclusiveAnnual) {
        mobileKey = ServiceNameKeys.mobileServiceFeeAllInclusiveSubscription;
      } else if (hasMonthly || hasAnnual) {
        mobileKey = ServiceNameKeys.mobileServiceFeeSubscription;
      } else if (vendorId === 1) {
        if (selectedServices
          .some(
            (s) => s.serviceTypeName === ServiceType.oilChange,
          )) {
          mobileKey = ServiceNameKeys.mobileServiceFeeAMZMainService;
        } else {
          mobileKey = ServiceNameKeys.mobileServiceFeeAMZ;
        }
      } else {
        mobileKey = ServiceNameKeys.mobileServiceFee;
      }

      selectedAddOns.forEach((addOn) => {
        if (
          typeof addOn.nameKey === 'string'
          && isMobileVariant(addOn.nameKey)
          && addOn.nameKey !== mobileKey
        ) {
          dispatch(removeAddOn(addOn.id));
        }
      });

      const mobileFee = data.getAddOnSurchargePrices.find(
        (addOn: AddOn) => addOn.nameKey === mobileKey,
      );

      const alreadyAddedChosenMobile = selectedAddOns.some(
        (addOn) => addOn.nameKey === mobileKey,
      );

      if (mobileFee && !alreadyAddedChosenMobile) {
        addAddOnPriced(mobileFee);
      }

      if (!mobileFee && alreadyAddedChosenMobile) {
        const existing = selectedAddOns.find((a) => a.nameKey === mobileKey);
        if (existing) {
          dispatch(removeAddOn(existing.id));
        }
      }
    }
    if (data && vendorId) {
      if (vendorId === 1) {
        const uBolt = data.getAddOnSurchargePrices.find(
          (addOn: AddOn) => addOn.nameKey === ServiceNameKeys.uBoltRetorque,
        );

        // Check if U-Bolt Retorque should be added
        if (
          selectedServices.some((service) => service.nameKey
          === ServiceNameKeys.engineAirFilterReplacement)
          && !selectedAddOns.some((addOn) => addOn.id === uBolt?.id)
        ) {
          dispatch(addAddOn(uBolt));
        } else if (!selectedServices.some((service) => service.nameKey
        === ServiceNameKeys.engineAirFilterReplacement)
          && selectedAddOns.some((addOn) => addOn.id === uBolt?.id)) {
          dispatch(removeAddOn(uBolt.id));
        }
      }
    }
    if (data && !vendorId) {
      // Same-day surcharge/Priority logic
      const sameDaySurcharge = data.getAddOnSurchargePrices.find(
        (addOn: AddOn) => addOn.nameKey === ServiceNameKeys.priorityServiceFee,
      );
      const alreadyAddedSameDay = sameDaySurcharge && selectedAddOns.some(
        (addOn: AddOn) => addOn.id === sameDaySurcharge.id,
      );
      // Today where the service happens, not where the browser is.
      const sameDay = sameDayFeeAllowed && isSlotToday(date);

      if (sameDaySurcharge && sameDay && !alreadyAddedSameDay) {
        addAddOnPriced(sameDaySurcharge);
      } else if (!sameDay && alreadyAddedSameDay) {
        dispatch(removeAddOn(sameDaySurcharge.id));
      }
    }
  }, [dispatch, data, selectedAddOns, selectedServices, vendorId,
    addAddOnPriced, sameDayFeeAllowed]);

  return {
    /** The fee rows (GetAddOnSurchargePrices), once loaded. */
    feesData: data as { getAddOnSurchargePrices: AddOn[] } | undefined,
    feeCatalog: (data?.getAddOnSurchargePrices ?? []) as AddOn[],
    applySlotFees,
  };
}
